import { useEffect, useMemo, useState } from "react";
import CachedIcon from "@mui/icons-material/Cached";
import { IconButton, Paper, Theme, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { FormattedMessage, useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "react-query";
import { atom, DefaultValue, selector, useRecoilState } from "recoil";
import { makeStyles } from "tss-react/mui";

import ResponsiveDialog from "../../components/ResponsiveDialog";
import { Row } from "../../Layout/utils";
import {
  ComparisonOptions,
  DateOptions,
  deleteGroup,
  Filter,
  FilterGroup,
  getFavoriteGroup,
  listGroups,
  PickupOptions,
  StatusOptions,
} from "../../services/Alert";
import { Module } from "../../services/Module";
import { FilterGroupCreate } from "../FilterGroup/FilterGroupCreate";
import { FilterGroupList } from "../FilterGroup/FilterGroupList";
import { FilterGroupName } from "../FilterGroup/FilterGroupName";
import { FilterGroupRow } from "../FilterGroup/FilterGroupRow";
import { FilterGroupSave } from "../FilterGroup/FilterGroupSave";
import { AddPerimeterFilter } from "./AddPerimeterFilter";
import { CreateFilterDateForm, MODE } from "./CreateFilterDateForm";
import { CreateFilterForm, FilterDimensionForm } from "./CreateFilterForm";
import { CreateFilterStatusForm } from "./CreateFilterStatusForm";
import Expand from "./Expand";
import {
  ComparisonChip,
  DateChip,
  DateRangeChip,
  FilterChip,
  FilterList,
  PickupChip,
} from "./FilterList";
import { FilterRow } from "./FilterRow";
import {
  FilterComparison,
  FilterDate,
  FilterDateRange,
  FilterDimension,
  FilterPickup,
  FilterStatus,
  FilterType,
} from "./FilterType";
import { ResetButton } from "./ResetButton";
import { getDefaultGroup } from "./utils";
import { CreateFilterPickupForm } from "./CreateFilterPickupForm";
import { CreateFilterComparisonForm } from "./CreateFilterComparisonForm";
import { GenericObject } from "../../services/Price";
import { DimensionValue } from "../../services/Category";

const useStyles = makeStyles()((theme: Theme) => ({
  paper: {
    background: theme.vars.palette.filter.background,
  },
}));

export const Title = styled(Typography)(({ theme }: { theme: Theme }) => ({
  color: theme.vars.palette.filter.text,
  padding: theme.spacing(1),
  minWidth: "168px",
}));

const CreateForm = ({
  filter,
  onSubmit,
  moduleSlug,
}: {
  filter:
    | FilterDimension
    | FilterDate
    | FilterStatus
    | FilterPickup
    | FilterComparison
    | FilterDateRange;
  onSubmit: (
    data:
      | FilterDimension
      | FilterDate
      | FilterStatus
      | FilterPickup
      | FilterComparison
      | FilterDateRange
  ) => void;
  moduleSlug: Module;
}) => {
  if (
    filter.type === FilterType.MULTIPLE ||
    filter.type === FilterType.DATE_RANGE
  ) {
    return (
      <CreateFilterForm
        model={{
          target: filter.target,
          values: filter.options.values,
        }}
        onSubmit={({ target, values, type }: Required<FilterDimensionForm>) =>
          // The user may switch to a dimension of another type while editing,
          // so the filter shape follows the submitted dimension's type, not
          // the original filter's.
          onSubmit(
            type === FilterType.DATE_RANGE
              ? {
                  type: FilterType.DATE_RANGE,
                  target,
                  options: { values: values as string[] },
                }
              : {
                  type: FilterType.MULTIPLE,
                  target,
                  options: {
                    __typename: "dimension",
                    values: values as DimensionValue[],
                  },
                }
          )
        }
        moduleSlug={moduleSlug}
      />
    );
  } else if (filter.type === FilterType.STATUS) {
    return (
      <CreateFilterStatusForm
        data={filter.options}
        onSubmit={(options: StatusOptions) =>
          // status is arbitrary defined as 10000
          onSubmit({ target: 10000, type: FilterType.STATUS, options })
        }
      />
    );
  } else if (filter.type === FilterType.PICKUP) {
    return (
      <>
        <CreateFilterPickupForm
          data={filter.options}
          onSubmit={(options: PickupOptions) =>
            onSubmit({ target: null, type: FilterType.PICKUP, options })
          }
        />
      </>
    );
  } else if (filter.type === FilterType.COMPARISON) {
    return (
      <CreateFilterComparisonForm
        data={filter.options}
        onSubmit={(options: ComparisonOptions) =>
          onSubmit({ target: null, type: FilterType.COMPARISON, options })
        }
      />
    );
  }

  return (
    <>
      <CreateFilterDateForm
        data={filter.options}
        onSubmit={(options: DateOptions) =>
          onSubmit({ target: null, type: FilterType.DATE, options })
        }
        moduleSlug={moduleSlug}
      />
    </>
  );
};

const FilterListItemDimension = ({
  filter,
  onChange,
  onDelete,
  moduleSlug,
}: {
  filter: Filter;
  onChange: (originalFilter: Filter, updatedFilter: Filter) => boolean;
  onDelete: (filter: Filter) => void;
  moduleSlug: Module;
}) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);

  const onSubmit = (updatedFilter: Filter) => {
    if (onChange(filter, updatedFilter)) {
      setOpen(false);
    } else {
      enqueueSnackbar(intl.formatMessage({ id: "FILTER_ALREADY_EXISTS" }), {
        variant: "error",
      });
    }
  };

  return (
    <>
      {filter.type === FilterType.DATE && (
        <DateChip
          key="date"
          filter={filter as FilterDate}
          onClick={() => setOpen(true)}
        />
      )}
      {filter.type === FilterType.PICKUP && (
        <PickupChip
          key="date"
          filter={filter as FilterPickup}
          onClick={() => setOpen(true)}
        />
      )}
      {filter.type === FilterType.COMPARISON && (
        <ComparisonChip
          key="comparison"
          filter={filter as FilterComparison}
          onClick={() => setOpen(true)}
        />
      )}
      {/* We do not want to force the filter for now */}
      {/*{filter.type === "status" && (*/}
      {/*  <StatusChip*/}
      {/*    key="status"*/}
      {/*    filter={filter as FilterStatus}*/}
      {/*    onClick={() => setOpen(true)}*/}
      {/*  />*/}
      {/*)}*/}
      {filter.type === FilterType.MULTIPLE && (
        <FilterChip
          key={filter.target}
          filter={filter as FilterDimension}
          onClick={() => setOpen(true)}
          onDelete={() => onDelete(filter)}
          moduleSlug={moduleSlug}
        />
      )}
      {filter.type === FilterType.DATE_RANGE && (
        <DateRangeChip
          key="date_range"
          filter={filter as FilterDateRange}
          onClick={() => setOpen(true)}
          onDelete={() => onDelete(filter)}
        />
      )}
      <ResponsiveDialog
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={() => setOpen(false)}
        title={intl.formatMessage({ id: "MODIFY_DIMENSION_FILTER" })}
        formId="create-filter-form"
      >
        <CreateForm
          filter={
            filter as
              | FilterDate
              | FilterDimension
              | FilterStatus
              | FilterPickup
              | FilterComparison
              | FilterDateRange
          }
          onSubmit={onSubmit}
          moduleSlug={moduleSlug}
        />
      </ResponsiveDialog>
    </>
  );
};

export const filtersAtom = atom<{ dimensions: Filter[]; kpis: Filter[] }>({
  key: "filters",
  default: {
    dimensions: [],
    kpis: [],
  },
});

export const dimensionsSelector = selector<Filter[]>({
  key: "dimensions",
  get: ({ get }) => get(filtersAtom).dimensions,
  set: ({ get, set }, newValue) =>
    set(filtersAtom, {
      ...get(filtersAtom),
      dimensions: newValue instanceof DefaultValue ? [] : newValue,
    }),
});

/** Force the perimeter's stay-date window to `mode`, for a view that opens on
 *  its own default window rather than the saved group's (the reservation list
 *  opens on the rolling week). Prepends the filter when the group has none,
 *  matching the order useFiltersUrl decodes filters in. */
const withDefaultDateMode = (filters: Filter[], mode: MODE): Filter[] => {
  const dateFilter = {
    target: null,
    type: FilterType.DATE,
    options: { __typename: "date", type: mode } as DateOptions,
  } as FilterDate;

  if (!filters.some((filter: Filter) => filter.type === FilterType.DATE)) {
    return [dateFilter, ...filters];
  }
  return filters.map((filter: Filter) =>
    filter.type === FilterType.DATE ? dateFilter : filter
  );
};

export const PerimeterFilter = ({
  showSubmitButton,
  moduleSlug,
  skipDefaultGroup,
  hiddenFilterTypes,
  defaultDateMode,
}: {
  showSubmitButton?: boolean;
  moduleSlug: Module;
  /** The page opened on a shared URL: its perimeter is authoritative, even
   *  empty (a KPI-only URL), so the default group is never applied. */
  skipDefaultGroup?: boolean;
  /** Filters kept in the perimeter but not shown as chips, for a filter
   *  only one view of the module reads (the pickup tabs' comparison date). */
  hiddenFilterTypes?: ReadonlySet<FilterType>;
  /** Stay-date window this view opens on, overriding the one saved on the
   *  perimeter group. URL filters still win over it. */
  defaultDateMode?: MODE;
}) => {
  const intl = useIntl();
  const { classes } = useStyles();
  const [selectedGroup, setSelectedGroup] = useState<FilterGroup | null>(null);
  const [filters, setFilters] = useRecoilState(dimensionsSelector);
  const [tmpFilters, setTmpFilters] = useState<Filter[]>(filters);

  const query = useQuery(["groups", "perimeter", moduleSlug], () =>
    listGroups("perimeter", moduleSlug)
  );

  const deleteMutation = useMutation(
    (groupId: number) => deleteGroup(groupId),
    {
      onSuccess: (data) => {
        query.refetch().then();
      },
    }
  );

  const updateFilters = (newFilters: Filter[]) => {
    if (showSubmitButton) {
      setTmpFilters(newFilters);
    } else {
      setFilters(newFilters);
    }
  };

  const addFilter = (filter: Filter) => {
    const filters = selectedGroup?.filters || [];
    const newFilters = [...filters, filter];

    setSelectedGroup((prev) =>
      prev ? { ...prev, ...{ filters: newFilters } } : prev
    );
    updateFilters(newFilters);
  };

  const removeFilter = (filterToRemove: Filter) => {
    const newFilters =
      selectedGroup?.filters.filter(
        (filter: Filter) => filter.target !== filterToRemove.target
      ) || [];
    setSelectedGroup((prev) =>
      prev ? { ...prev, ...{ filters: newFilters } } : prev
    );
    updateFilters(newFilters);
  };

  const resetFilters = () => {
    setSelectedGroup((prev) => {
      if (prev && query.data) {
        const defaultFilters = query.data.results.find(
          (el: GenericObject) => el.name === prev.name
        );
        if (defaultFilters) {
          // Reset returns to the view's opening state, so it re-applies the
          // default window rather than restoring the group's saved one.
          const next = defaultDateMode
            ? withDefaultDateMode(defaultFilters.filters, defaultDateMode)
            : defaultFilters.filters;
          updateFilters(next);
          return { ...defaultFilters, filters: next };
        }
      }
      return prev;
    });
  };

  // Replacement is keyed on the ORIGINAL filter's identity, not the updated
  // one's: editing may change the dimension (target/type), in which case the
  // updated filter matches nothing and the change would be silently lost.
  const onChangeFilter = (originalFilter: Filter, updatedFilter: Filter) => {
    const filters = selectedGroup?.filters || [];
    const matches = (a: Filter, b: Filter) =>
      a.type === b.type && a.target === b.target;

    if (
      !matches(originalFilter, updatedFilter) &&
      filters.some((filter: Filter) => matches(filter, updatedFilter))
    ) {
      return false;
    }

    const newFilters = filters.map((filter: Filter) =>
      matches(filter, originalFilter) ? updatedFilter : filter
    );
    setSelectedGroup((prev) =>
      prev ? { ...prev, ...{ filters: newFilters } } : prev
    );
    updateFilters(newFilters);
    return true;
  };

  const saveGroup = (group: FilterGroup) => {
    query.refetch().then(() => setSelectedGroup(group));
  };

  const handleCreate = (group: FilterGroup) => {
    query.refetch().then(() => setSelectedGroup(group));
  };

  const handleDelete = (group: FilterGroup) => {
    if (selectedGroup?.id === group.id) {
      setSelectedGroup(null);
    }
    deleteMutation.mutate(group.id);
  };

  const defaultGroupName = intl.formatMessage({
    id: "DEFAULT_PARAMETER_FILTER_GROUP_NAME",
  });

  const defaultGroup = useMemo(() => {
    if (!query.data) return null;
    const favorite = getFavoriteGroup(query.data.results as any);
    return (
      favorite ||
      query.data.results[0] ||
      getDefaultGroup(defaultGroupName, "perimeter")
    );
  }, [query.data, defaultGroupName]);

  // Memoized, not derived inside the effect below: that effect reads `filters`,
  // so seeding recoil with a freshly built array on every run would retrigger
  // it forever (recoil forces a synchronous re-render, which lands before the
  // queued setSelectedGroup commits, so the `!selectedGroup` guard never
  // closes). A stable identity lets recoil bail out on the second write.
  const defaultGroupFilters = useMemo(
    () =>
      defaultGroup && defaultDateMode
        ? withDefaultDateMode(defaultGroup.filters, defaultDateMode)
        : defaultGroup?.filters,
    [defaultGroup, defaultDateMode]
  );

  useEffect(() => {
    if (defaultGroup && defaultGroupFilters && !selectedGroup) {
      // Adopt the perimeter as it is when the URL drives it (even empty) or
      // when a sibling hook already seeded it during the same synchronous
      // recoil render. Overwriting it would re-run this effect before
      // setSelectedGroup commits, and overwriting a sibling's write loops.
      const keepPerimeter = skipDefaultGroup || filters.length > 0;
      setSelectedGroup({
        ...defaultGroup,
        filters: keepPerimeter ? [...filters] : defaultGroupFilters,
      });
      if (!keepPerimeter) {
        setFilters(defaultGroupFilters);
      }
    }
  }, [
    defaultGroup,
    defaultGroupFilters,
    selectedGroup,
    setFilters,
    skipDefaultGroup,
    filters,
  ]);

  const visibleFilters = useMemo(
    () =>
      (selectedGroup?.filters ?? []).filter(
        (filter: Filter) => !hiddenFilterTypes?.has(filter.type)
      ),
    [selectedGroup, hiddenFilterTypes]
  );

  const onChangeSelectedGroup = (filterGroup: FilterGroup) => {
    setSelectedGroup(filterGroup);
    setFilters(filterGroup.filters);
  };

  useEffect(() => {
    setTmpFilters(filters);
    setSelectedGroup((prev) =>
      prev ? { ...prev, filters: [...filters] } : prev
    );
  }, [filters, setTmpFilters]);

  return (
    <>
      {query.data && selectedGroup && (
        <Paper className={`${classes.paper}`}>
          <Row>
            <FilterRow>
              <Title style={{ fontSize: "initial" }}>
                <FormattedMessage id="PERIMETER_CHOICE" />
              </Title>
              <ResetButton onClick={resetFilters} />
              {selectedGroup && (
                <Row
                  sx={{
                    alignItems: "center",
                    gap: 1,
                    overflow: "hidden",
                  }}
                >
                  <Expand>
                    <FilterList filters={visibleFilters}>
                      {(filter, index) => (
                        <FilterListItemDimension
                          key={index}
                          filter={filter}
                          onChange={onChangeFilter}
                          onDelete={removeFilter}
                          moduleSlug={moduleSlug}
                        />
                      )}
                    </FilterList>
                  </Expand>
                  <AddPerimeterFilter
                    filters={selectedGroup.filters as FilterDimension[]}
                    onSubmit={addFilter}
                    moduleSlug={moduleSlug}
                  />
                </Row>
              )}
            </FilterRow>
            <FilterGroupRow title={"Périmètre géographique et temporel"}>
              {showSubmitButton && (
                <IconButton
                  onClick={() => setFilters(tmpFilters)}
                  size="large"
                  sx={{ color: "text.accentInverted" }}
                >
                  <CachedIcon fontSize="small" />
                </IconButton>
              )}

              <FilterGroupList
                groups={query.data.results}
                onChange={onChangeSelectedGroup}
                onDelete={handleDelete}
                onUpdate={() => query.refetch().then()}
                selectedGroup={selectedGroup}
              />
              <FilterGroupName name={selectedGroup.name} />
              <FilterGroupCreate
                group={selectedGroup}
                onCreate={handleCreate}
                moduleSlug={moduleSlug}
              />
              <FilterGroupSave
                group={selectedGroup}
                onUpdate={(group) => saveGroup(group)}
              />
            </FilterGroupRow>
          </Row>
        </Paper>
      )}
    </>
  );
};
